// 负责商品目录主视图，与商品筛选、商品详情和信任说明模块关联。
import { Check, ChevronDown, Search, SlidersHorizontal, Truck } from "lucide-react";
import type { SortMode } from "../../app/useStorefront";
import type { Product, ProductCategory } from "../../domain/products";
import { ProductCard } from "./ProductCard";
import { ProductDetail } from "./ProductDetail";

type CatalogViewProps = {
  categories: Array<ProductCategory | "All">;
  query: string;
  selectedCategory: ProductCategory | "All";
  selectedProduct: Product;
  selectedSize: string;
  sortMode: SortMode;
  visibleProducts: Product[];
  onAddSelectedProduct: () => void;
  onCategoryChange: (category: ProductCategory | "All") => void;
  onQueryChange: (query: string) => void;
  onSelectProduct: (product: Product) => void;
  onSelectSize: (size: string) => void;
  onSortModeChange: (sortMode: SortMode) => void;
};

export const CatalogView = ({
  categories,
  query,
  selectedCategory,
  selectedProduct,
  selectedSize,
  sortMode,
  visibleProducts,
  onAddSelectedProduct,
  onCategoryChange,
  onQueryChange,
  onSelectProduct,
  onSelectSize,
  onSortModeChange
}: CatalogViewProps) => {
  return (
    <>
      <section className="storefront" id="catalog">
        <aside className="editorial-panel" aria-label="Current collection">
          <img
            src="https://images.unsplash.com/photo-1509631179647-0177331693ae?auto=format&fit=crop&w=1200&q=80"
            alt="Model wearing a neutral women's fashion look"
          />
          <div className="collection-copy">
            <p>Spring capsule</p>
            <h1>Travel-light pieces for cross-border DTC testing.</h1>
            <span>
              Mobile-first catalog, clear fit notes, and hosted checkout readiness.
            </span>
          </div>
        </aside>

        <section className="catalog-area" aria-label="Product catalog">
          <div className="catalog-toolbar">
            <label className="search-box">
              <Search aria-hidden="true" size={18} />
              <input
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="Search dresses, tops, colors"
              />
            </label>

            <label className="select-box">
              <SlidersHorizontal aria-hidden="true" size={18} />
              <select
                value={sortMode}
                onChange={(event) =>
                  onSortModeChange(event.target.value as SortMode)
                }
              >
                <option>Featured</option>
                <option>Price: Low</option>
                <option>Price: High</option>
              </select>
              <ChevronDown aria-hidden="true" size={16} />
            </label>
          </div>

          <div className="category-tabs" role="tablist" aria-label="Categories">
            {categories.map((category) => (
              <button
                className={category === selectedCategory ? "active" : ""}
                type="button"
                role="tab"
                aria-selected={category === selectedCategory}
                key={category}
                onClick={() => onCategoryChange(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="product-layout">
            <div className="product-grid">
              {visibleProducts.map((product) => (
                <ProductCard
                  product={product}
                  isSelected={product.id === selectedProduct.id}
                  key={product.id}
                  onSelect={onSelectProduct}
                />
              ))}
            </div>

            <ProductDetail
              product={selectedProduct}
              selectedSize={selectedSize}
              onSelectSize={onSelectSize}
              onAddSelectedProduct={onAddSelectedProduct}
            />
          </div>
        </section>
      </section>

      <section className="trust-band" id="fit" aria-label="Shopping policies">
        <div>
          <Check aria-hidden="true" />
          <h2>International size guidance</h2>
          <p>
            Every product keeps fit notes, size buttons, and material data visible
            near purchase.
          </p>
        </div>
        <div id="shipping">
          <Truck aria-hidden="true" />
          <h2>Cross-border shipping copy</h2>
          <p>
            Shipping and returns are promoted in-cart and on product pages for
            trust-building tests.
          </p>
        </div>
      </section>
    </>
  );
};
